import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Section } from '../shared';

// Twinkling Star Rating
const TwinklingStar = ({ filled, index }) => {
  return (
    <motion.span
      className={`text-xl inline-block ${filled ? 'text-neon-orange' : 'text-gray-600'}`}
      animate={filled ? {
        scale: [1, 1.2, 1],
        opacity: [0.8, 1, 0.8],
        textShadow: [
          '0 0 0px rgba(253, 128, 46, 0)',
          '0 0 10px rgba(253, 128, 46, 0.8)',
          '0 0 0px rgba(253, 128, 46, 0)'
        ]
      } : {}}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        delay: index * 0.2
      }}
    >
      ★
    </motion.span>
  );
};

// Star Rating Component
const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <TwinklingStar key={i} filled={i < rating} index={i} />
      ))}
    </div>
  );
};

// Floating Quote Bubbles
const FloatingQuoteBubbles = () => {
  const bubbles = [
    { x: '10%', y: '20%', size: 40, delay: 0 },
    { x: '85%', y: '15%', size: 30, delay: 1 },
    { x: '75%', y: '70%', size: 50, delay: 2 },
    { x: '15%', y: '75%', size: 35, delay: 1.5 },
    { x: '50%', y: '85%', size: 25, delay: 0.5 },
    { x: '90%', y: '45%', size: 28, delay: 2.5 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {bubbles.map((bubble, i) => (
        <motion.div
          key={i}
          className="absolute text-neon-orange/10"
          style={{
            left: bubble.x,
            top: bubble.y,
            fontSize: bubble.size
          }}
          animate={{
            y: [-10, 10, -10],
            opacity: [0.05, 0.15, 0.05],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 4 + Math.random() * 2,
            repeat: Infinity,
            delay: bubble.delay,
          }}
        >
          "
        </motion.div>
      ))}
    </div>
  );
};

// Navigation Arrow Component
const NavArrow = ({ direction, onClick, disabled }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${
        disabled 
          ? 'border-gray-700 text-gray-700 cursor-not-allowed' 
          : 'border-neon-orange/30 text-neon-orange hover:border-neon-orange'
      }`}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={!disabled ? { scale: 1.1 } : {}}
      whileTap={!disabled ? { scale: 0.95 } : {}}
      animate={!disabled && isHovered ? {
        boxShadow: [
          '0 0 0px rgba(253, 128, 46, 0)',
          '0 0 20px rgba(253, 128, 46, 0.5)',
          '0 0 0px rgba(253, 128, 46, 0)'
        ]
      } : {}}
      transition={{ duration: 0.8, repeat: isHovered ? Infinity : 0 }}
    >
      <svg 
        className="w-6 h-6" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} 
        />
      </svg>
    </motion.button>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ testimonial, isActive }) => {
  return (
    <motion.div
      className={`bg-dark-deeper/80 backdrop-blur rounded-2xl p-8 border transition-all h-full ${
        isActive 
          ? 'border-neon-orange/50 shadow-[0_0_30px_rgba(253,128,46,0.15)]' 
          : 'border-neon-orange/10'
      }`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
    >
      {/* Star Rating */}
      <StarRating rating={testimonial.rating} />
      
      {/* Quote */}
      <div className="relative mb-6">
        <span className="absolute -top-4 -left-2 text-neon-orange/20 text-5xl font-serif">"</span>
        <p className="text-gray-300 italic text-lg leading-relaxed pl-4">
          {testimonial.text}
        </p>
        <span className="absolute -bottom-4 right-0 text-neon-orange/20 text-5xl font-serif">"</span>
      </div>
      
      {/* Author */}
      <div className="flex items-center gap-4 mt-8">
        {/* Avatar with neon ring */}
        <div className="relative">
          <motion.div
            className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-orange to-neon-orange-light"
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ padding: '2px', margin: '-2px' }}
          />
          <img 
            src={testimonial.avatar} 
            alt={testimonial.name}
            className="w-14 h-14 rounded-full border-2 border-neon-orange relative z-10 object-cover"
          />
        </div>
        <div>
          <div className="text-white font-semibold">{testimonial.name}</div>
          <div className="text-gray-500 text-sm">{testimonial.role}</div>
          {testimonial.company && (
            <div className="text-neon-orange/70 text-xs">{testimonial.company}</div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Progress Indicator
const ProgressIndicator = ({ total, current, onSelect }) => {
  return (
    <div className="flex justify-center gap-2 mt-8">
      {[...Array(total)].map((_, i) => (
        <button
          key={i}
          onClick={() => onSelect(i)}
          className="group relative"
        >
          <motion.div
            className={`w-3 h-3 rounded-full transition-all ${
              i === current ? 'bg-neon-orange' : 'bg-neon-orange/30'
            }`}
            whileHover={{ scale: 1.5 }}
          />
          {i === current && (
            <motion.div
              className="absolute inset-0 rounded-full bg-neon-orange"
              animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
        </button>
      ))}
    </div>
  );
};

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const autoPlayRef = useRef(null);

  const testimonials = [
    {
      name: "Amara Solis",
      role: "Podcast Producer",
      company: "The Daily Brief",
      avatar: "/Testimonial_result.webp",
      text: "SoundFlowAI cut my editing time by 75%. The noise reduction alone is worth the subscription. I used to spend 4 hours per episode—now it's under an hour.",
      rating: 5
    },
    {
      name: "Eamon Rix",
      role: "Music Producer",
      company: "Beats & Beyond Studio",
      avatar: "/Testimonial%20(5)_result.webp",
      text: "Finally, an AI tool that understands music. The stem separation is incredible, and the mixing suggestions are spot-on. It's like having a pro engineer on call 24/7.",
      rating: 5
    },
    {
      name: "Tavia Quinn",
      role: "Sound Designer",
      company: "GameAudio Labs",
      avatar: "/Testimonial%20(3)_result.webp",
      text: "I've tried every audio tool out there. Nothing comes close to the workflow automation this platform offers. Generated 200+ sound effects in a single afternoon.",
      rating: 5
    },
    {
      name: "Kael Dorian",
      role: "Content Creator",
      company: "2.5M YouTube Subscribers",
      avatar: "/Testimonial%20(4)_result.webp",
      text: "My audience immediately noticed the audio quality improvement. The auto-ducking and voice enhancement features are game-changers for video creators.",
      rating: 5
    },
    {
      name: "Selena Lane",
      role: "Audio Engineer",
      company: "Broadcast Media Inc",
      avatar: "/Testimonial%20(2)_result.webp",
      text: "We process thousands of audio files weekly. The batch processing and API integration saved our team countless hours. ROI was visible within the first month.",
      rating: 5
    },
  ];

  // Auto-advance carousel
  useEffect(() => {
    if (!isPaused) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPaused, testimonials.length]);

  // Navigation handlers
  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
  };

  // Get visible testimonials for carousel
  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + testimonials.length) % testimonials.length;
      visible.push({ ...testimonials[index], position: i });
    }
    return visible;
  };

  return (
    <Section id="testimonials" className="bg-dark-base relative overflow-hidden">
      {/* Floating Quote Bubbles */}
      <FloatingQuoteBubbles />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.span 
            className="text-neon-orange font-semibold text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Testimonials
          </motion.span>
          
          <motion.h2 
            className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Loved by{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light">
              Audio Professionals
            </span>
          </motion.h2>
        </div>

        {/* Carousel Container */}
        <div 
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Navigation Arrows - Desktop */}
          <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-0 right-0 justify-between z-20 px-4">
            <NavArrow direction="left" onClick={goToPrev} />
            <NavArrow direction="right" onClick={goToNext} />
          </div>

          {/* Cards Container */}
          <div className="overflow-hidden px-4 md:px-20">
            <div className="flex items-center justify-center min-h-[400px]">
              <AnimatePresence mode="wait">
                {/* Mobile: Single Card */}
                <div className="md:hidden w-full">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TestimonialCard 
                      testimonial={testimonials[currentIndex]} 
                      isActive={true}
                    />
                  </motion.div>
                </div>

                {/* Desktop: Three Cards */}
                <div className="hidden md:flex gap-6 items-stretch">
                  {getVisibleTestimonials().map((testimonial, i) => (
                    <motion.div
                      key={`${currentIndex}-${i}`}
                      className="w-[380px] flex-shrink-0"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ 
                        opacity: testimonial.position === 0 ? 1 : 0.5,
                        scale: testimonial.position === 0 ? 1 : 0.9,
                        y: testimonial.position === 0 ? 0 : 20,
                      }}
                      transition={{ duration: 0.4 }}
                    >
                      <TestimonialCard 
                        testimonial={testimonial} 
                        isActive={testimonial.position === 0}
                      />
                    </motion.div>
                  ))}
                </div>
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Navigation Arrows */}
          <div className="md:hidden flex justify-center gap-8 mt-6">
            <NavArrow direction="left" onClick={goToPrev} />
            <NavArrow direction="right" onClick={goToNext} />
          </div>

          {/* Progress Indicator */}
          <ProgressIndicator 
            total={testimonials.length} 
            current={currentIndex}
            onSelect={setCurrentIndex}
          />

          {/* Pause Indicator */}
          {isPaused && (
            <motion.div 
              className="text-center text-gray-500 text-sm mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Paused • Hover to resume auto-play
            </motion.div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default TestimonialsSection;
